import React from 'react';
import { Trans } from '@lingui/macro';
import { TableWrap } from './FeatureInfoDrawer';

// Body content for the Execution Conditions info drawer: how conditions gate an
// integration for a given event, plus worked examples (WMI validation, repair
// status, VIN-not-present, and dual-trigger integrations).
const ExecutionConditionsInfoContent = () => (
  <>
    <ul>
      <li>
        <Trans>
          Execution Conditions determine whether an integration runs for a given
          event. Each condition checks a specific field on the incoming payload
          against a rule you define.
        </Trans>
      </li>
      <li>
        <Trans>
          The attribute is a dot-path to the field in the payload. For
          application events the path is nested (e.g.,{' '}
          <code>data.asset.vin</code>
          ); for HTTP triggers it is usually root-level (e.g., <code>vin</code>
          ).
        </Trans>
      </li>
      <li>
        <Trans>
          The Execution Conditions Type below controls how multiple conditions
          combine: AND requires every condition to match, OR requires only one.
          When the combined conditions aren’t met, INF skips execution for that
          event. The request was received, but INF makes no call to the
          integrated system.
        </Trans>
      </li>
    </ul>

    <h4>
      <Trans>Examples</Trans>
    </h4>

    <p>
      <strong>
        <Trans>WMI Validation</Trans>
      </strong>
    </p>
    <p>
      <Trans>
        WMI Validation checks the WMI (World Manufacturer Identifier, the
        leading characters of a VIN) against a WMI group defined in the toolkit,
        so an OEM-facing integration (Warranty, Campaigns, Service Contracts,
        etc.) only calls that manufacturer’s system for vehicles it actually
        built, instead of relying on the asset’s Make field. For example, this
        keeps the integration from querying Volvo’s systems for an Isuzu-built
        asset.
      </Trans>
    </p>
    <TableWrap>
      <table>
        <thead>
          <tr>
            <th>
              <Trans>Attribute</Trans>
            </th>
            <th>
              <Trans>Rule</Trans>
            </th>
            <th>
              <Trans>Value</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>vin</code>
            </td>
            <td>
              <Trans>WMI Validation</Trans>
            </td>
            <td>Isuzu</td>
          </tr>
        </tbody>
      </table>
    </TableWrap>
    <p>
      <Trans>
        A single integration can also cover more than one make from the same
        manufacturer group. Combine two WMI Validation conditions with Match ANY
        condition (OR) so either make satisfies the rule, for example a
        PACCAR-facing integration that should run for Kenworth or Peterbilt:
      </Trans>
    </p>
    <TableWrap>
      <table>
        <thead>
          <tr>
            <th>
              <Trans>Attribute</Trans>
            </th>
            <th>
              <Trans>Rule</Trans>
            </th>
            <th>
              <Trans>Value</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>data.vin</code>
            </td>
            <td>
              <Trans>WMI Validation</Trans>
            </td>
            <td>Kenworth</td>
          </tr>
          <tr>
            <td>
              <code>data.vin</code>
            </td>
            <td>
              <Trans>WMI Validation</Trans>
            </td>
            <td>Peterbilt</td>
          </tr>
        </tbody>
      </table>
      <p className="caption">
        <Trans>Execution Conditions Type: Match ANY condition (OR)</Trans>
      </p>
    </TableWrap>

    <p>
      <strong>
        <Trans>Repair status</Trans>
      </strong>
    </p>
    <p>
      <Trans>
        Runs the integration only when a case’s repair status changes to one of
        two values. Use Match ANY condition (OR), since a single event can’t
        equal both values at once:
      </Trans>
    </p>
    <TableWrap>
      <table>
        <thead>
          <tr>
            <th>
              <Trans>Attribute</Trans>
            </th>
            <th>
              <Trans>Rule</Trans>
            </th>
            <th>
              <Trans>Value</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>data.repair_status</code>
            </td>
            <td>
              <Trans>Equals to</Trans>
            </td>
            <td>
              <code>asset_ready</code>
            </td>
          </tr>
          <tr>
            <td>
              <code>data.repair_status</code>
            </td>
            <td>
              <Trans>Equals to</Trans>
            </td>
            <td>
              <code>asset_released</code>
            </td>
          </tr>
        </tbody>
      </table>
      <p className="caption">
        <Trans>Execution Conditions Type: Match ANY condition (OR)</Trans>
      </p>
    </TableWrap>

    <p>
      <strong>
        <Trans>VIN not present (Serial Based / non-VIN assets)</Trans>
      </strong>
    </p>
    <p>
      <Trans>
        Some assets, such as trailers or other equipment, are identified by a
        serial number instead of a VIN. Use Is Not Present to scope an
        integration to assets that don’t carry a VIN:
      </Trans>
    </p>
    <TableWrap>
      <table>
        <thead>
          <tr>
            <th>
              <Trans>Attribute</Trans>
            </th>
            <th>
              <Trans>Rule</Trans>
            </th>
            <th>
              <Trans>Value</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>vin</code>
            </td>
            <td>
              <Trans>Is Not Present</Trans>
            </td>
            <td>
              <Trans>
                Not applicable; this rule checks whether the attribute exists
                rather than comparing it to a value.
              </Trans>
            </td>
          </tr>
        </tbody>
      </table>
    </TableWrap>

    <p>
      <strong>
        <Trans>Integration with two trigger types</Trans>
      </strong>
    </p>
    <p>
      <Trans>
        An integration that can run from either an application event or a direct
        HTTP call needs one execution condition per trigger shape, since the
        same field lives at a different path depending on the trigger. Combine
        them with Match ANY condition (OR) so either trigger path satisfies the
        rule.
      </Trans>
    </p>
    <ul>
      <li>
        <Trans>Trigger</Trans>: <code>application_event</code>,{' '}
        <code>http_api_request</code>
      </li>
      <li>
        <Trans>Action</Trans>:{' '}
        <code>retrieve_manufacturer_build_information</code>
      </li>
      <li>
        <Trans>Application Events</Trans>:{' '}
        <code>decisiv:asset_management:registered_asset:created</code>,{' '}
        <code>decisiv:service_management:customer_asset:created</code>
      </li>
      <li>
        <Trans>Metadata</Trans>: <code>oem</code> = <code>pcar</code>
      </li>
    </ul>
    <TableWrap>
      <table>
        <thead>
          <tr>
            <th>
              <Trans>Attribute</Trans>
            </th>
            <th>
              <Trans>Rule</Trans>
            </th>
            <th>
              <Trans>Value</Trans>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <code>data.vin</code>
            </td>
            <td>
              <Trans>WMI Validation</Trans>
            </td>
            <td>
              <Trans>Paccar (Kenworth &amp; Peterbilt)</Trans>
            </td>
          </tr>
          <tr>
            <td>
              <code>vin</code>
            </td>
            <td>
              <Trans>WMI Validation</Trans>
            </td>
            <td>
              <Trans>Paccar (Kenworth &amp; Peterbilt)</Trans>
            </td>
          </tr>
        </tbody>
      </table>
      <p className="caption">
        <Trans>Execution Conditions Type: Match ANY condition (OR)</Trans>
      </p>
    </TableWrap>
  </>
);

export default ExecutionConditionsInfoContent;
